import Microgame from '../engine/Microgame.js';

export default class SpaceGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: 'Space !', en: 'Space!', es: '¡Espacio!' },
            controls: { fr: 'Maintiens appuyé pour propulser le vaisseau (PC: Flèches/Clic)', en: 'Hold to thrust the ship (PC: Arrows/Hold)', es: 'Mantén pulsado para propulsar la nave' },
            objective: { fr: 'Évite les astéroïdes et atteins la ligne d\'arrivée', en: 'Avoid asteroids and reach the finish line', es: 'Evita los asteroides y llega a la meta' },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path></svg>`,
            actions: [
                { type: 'hold', x: 50, y: 8, holdDuration: 2400 },
                { type: 'wait', duration: 500 },
            ],
            loopDuration: 3600
        };
    }
    constructor(engine) {
        super(engine);
        // Progressive difficulty balance
        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.3;
        
        // Timer scales inversely with difficulty
        this.duration = 4000 * (speedMult / adj);

        this.ship = { x: 50, y: 80, vx: 0, vy: 0 };
        
        // Speed affects both scrolling and thrust - Progression EXCESSIVEMENT douce
        // Objectif: x4.0 doit être équivalent à la précédente vitesse x3.5
        this.scrollSpeed = 0.4 + (speedMult - 1) * 0.02; 
        this.thrustForce = 0.06 + (speedMult - 1) * 0.02; 
        this.friction = 0.95; // Space inertia decay
        
        this.asteroids = [];
        this.baseAsteroidSpeed = 0.8 * speedMult;
        // Moderate density increase: caps even lower for x4
        this.spawnRate = 180 - (speedMult - 1) * 10; 
        this.spawnTimer = 0;
        
        this.targetAngle = 0;
        this.currentAngle = 0;
        
        this.isThrusting = false;
        this.targetPt = { x: 50, y: 0 };
        
        // Background and Finish Line
        this.stars = [];
        this.totalDistance = 1000; // Arbitrary units for progress
        this.distanceTraveled = 0;
        this.finishLineEl = null;
        this.finishLineReached = false;
        
        this.particles = [];
        this._isAnimating = false;
    }

    setup() {
        this.engine.ui.instruction.textContent = "PILOT!";

        this.containerStyle = getComputedStyle(this.container);
        this.containerWidth = this.container.clientWidth || 400;
        this.containerHeight = this.container.clientHeight || 400;

        // Player ship wrapper (no clipPath)
        this.shipEl = document.createElement('div');
        this.shipEl.style.position = 'absolute';
        this.shipEl.style.width = '30px';
        this.shipEl.style.height = '35px';
        this.shipEl.style.transformOrigin = 'center center';
        this.shipEl.style.transform = `translate(-50%, -50%) rotate(0deg)`;
        this.shipEl.style.zIndex = '10';
        this.container.appendChild(this.shipEl);

        // Ship visual body
        this.shipVis = document.createElement('div');
        this.shipVis.style.position = 'absolute';
        this.shipVis.style.width = '100%';
        this.shipVis.style.height = '100%';
        this.shipVis.style.background = this.primaryColor;
        this.shipVis.style.clipPath = 'polygon(50% 0%, 0% 100%, 50% 80%, 100% 100%)'; 
        this.shipVis.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.shipVis.style.transition = 'background 0.2s, box-shadow 0.2s';
        this.shipEl.appendChild(this.shipVis);

        // Flame element
        this.flameEl = document.createElement('div');
        this.flameEl.style.position = 'absolute';
        this.flameEl.style.bottom = '-15px';
        this.flameEl.style.left = '50%';
        this.flameEl.style.transform = 'translate(-50%, 0)';
        this.flameEl.style.width = '14px';
        this.flameEl.style.height = '20px';
        this.flameEl.style.background = this.secondaryColor;
        this.flameEl.style.clipPath = 'polygon(50% 100%, 20% 0%, 80% 0%)';
        this.flameEl.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.flameEl.style.opacity = '0';
        this.flameEl.style.transition = 'opacity 0.1s';
        this.shipEl.appendChild(this.flameEl);

        // Events
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);
        
        // Bind to input element for touch/mouse
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        window.addEventListener('pointerup', this.onUp); // window to catch early release

        // Stars background
        for (let i = 0; i < 40; i++) {
            const star = document.createElement('div');
            star.style.position = 'absolute';
            const size = Math.random() * 2 + 1;
            star.style.width = `${size}px`;
            star.style.height = `${size}px`;
            star.style.background = '#fff';
            star.style.borderRadius = '50%';
            star.style.opacity = Math.random() * 0.5 + 0.3;
            star.style.zIndex = '1';
            const x = Math.random() * 100;
            const y = Math.random() * 100;
            star.style.left = `${x}%`;
            star.style.top = `${y}%`;
            this.container.appendChild(star);
            this.stars.push({ el: star, x, y, speedMult: Math.random() * 0.5 + 0.5 });
        }

        // Finish Line: Neon colors
        this.finishLineEl = document.createElement('div');
        this.finishLineEl.style.position = 'absolute';
        this.finishLineEl.style.width = '110%';
        this.finishLineEl.style.height = '14px';
        this.finishLineEl.style.background = `repeating-linear-gradient(90deg, ${this.primaryColor} 0, ${this.primaryColor} 20px, ${this.secondaryColor} 20px, ${this.secondaryColor} 40px)`;
        this.finishLineEl.style.borderTop = `2px solid ${this.primaryColor}`;
        this.finishLineEl.style.borderBottom = `2px solid ${this.primaryColor}`;
        this.finishLineEl.style.boxShadow = `0 0 20px ${this.primaryColor}`;
        this.finishLineEl.style.left = '-5%';
        this.finishLineEl.style.zIndex = '5';
        this.container.appendChild(this.finishLineEl);

        // Distance Calculation: Reach ship's start Y (80%) at ~65% of duration
        // We use a much flatter distance progression to ensure high speeds are playable
        const currentSpeed = this.engine.speedMultiplier || 1.0;
        const ticksAtX1 = 4000 / 16.67;
        const baseDist = ticksAtX1 * 0.65 * 0.4; // = 62.4
        const distOffset = (currentSpeed - 1) * 9.6; // Adjusted factor to match current x3.5 at x4.0
        
        this.finishLineY = 80 - (baseDist + distOffset);
        this.finishLineEl.style.top = `${this.finishLineY}%`;

        // Pre-warm some asteroids on the screen
        for(let i=0; i<15; i++) {
            this.spawnAsteroid(true);
        }
    }

    updatePointer(e) {
        const rect = this.container.getBoundingClientRect();
        this.targetPt.x = ((e.clientX - rect.left) / rect.width) * 100;
        this.targetPt.y = ((e.clientY - rect.top) / rect.height) * 100;
    }

    onDown(e) {
        if (!this.active || this._isAnimating) return;
        this.isThrusting = true;
        this.updatePointer(e);
    }

    onMove(e) {
        if (!this.active || this._isAnimating) return;
        if (this.isThrusting) {
            this.updatePointer(e);
        }
    }

    onUp(e) {
        this.isThrusting = false;
    }

    spawnAsteroid(preWarm = false) {
        let size = 25 + Math.random() * 40; // px
        
        // Ensure playable gap: check for valid space
        let validSpawn = false;
        let attempts = 0;
        let x, y;

        while (!validSpawn && attempts < 10) {
            // Spawn horizontally wide to allow edges
            x = -5 + Math.random() * 110;
            
            // Safety Corridor: No asteroids in the ship's direct path at the very start
            if (preWarm && Math.abs(x - 50) < 12) {
                attempts++;
                continue; 
            }
            
            // If pre-warming, spawn in the top half of the screen MAX to avoid spawn kill (Ship is at 80%)
            // Otherwise, spawn well above the top edge
            y = preWarm ? (Math.random() * 60 - 30) : -35;

            validSpawn = true;
            for (let a of this.asteroids) {
                // If within the same horizontal band (~15% height or ~60px)
                if (Math.abs(a.y - y) < 15) {
                    // Ensure a safe horizontal distance. 32% = ~128px center to center.
                    // Max asteroid size is 65px, so 128 - 65 = 63px gap edge-to-edge (Ship is 30px, so 2x width minimum).
                    if (Math.abs(a.x - x) < 32) {
                        validSpawn = false;
                        break;
                    }
                }
            }
            attempts++;
        }

        // If we couldn't find a spot that leaves a path, don't spawn this asteroid (naturally regulates density)
        if (!validSpawn) return;

        // Small horizontal drift, tiny vertical drift variance
        let driftX = (Math.random() - 0.5) * 0.2 * this.engine.speedMultiplier;
        let driftY = (Math.random() * 0.3) * this.engine.speedMultiplier;
        
        let el = document.createElement('div');
        el.style.position = 'absolute';
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.background = 'transparent';
        el.style.border = `2px solid ${this.secondaryColor}`;
        el.style.borderRadius = '40% 60% 70% 30% / 40% 50% 60% 50%';
        el.style.transform = `translate(-50%, -50%) rotate(${Math.random()*360}deg)`;
        el.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        
        this.container.appendChild(el);
        this.asteroids.push({ el, x, y, dx: driftX, dy: driftY, size: size, rot: Math.random()*360, rotSpeed: (Math.random()-0.5)*3 });
    }

    update(dt) {
        if (!this.active) return;
        
        const timeFactor = dt / 16.67;

        // 1. Particle update (Explosion/Trail)
        this.updateParticles(timeFactor);

        if (this._isAnimating && !this.finishLineReached) {
            // IMPORTANT: Neutralize GameEngine's timer countdown so it doesn't kill us mid-animation
            if (this.engine.timer !== Infinity) {
                this.engine.timer += dt; 
            }
            // Keep updating background and existing elements during death sequence
            this.updateBackground(timeFactor);
            this.updateAsteroids(timeFactor);
            return;
        }

        // 1. Calculate Thrust
        if (this.isThrusting) {
            const dx = this.targetPt.x - this.ship.x;
            const dy = this.targetPt.y - this.ship.y;
            const dist = Math.sqrt(dx*dx + dy*dy);
            
            // Avoid jittering if finger is exactly on the center
            if (dist > 2) {
                this.ship.vx += (dx / dist) * this.thrustForce * timeFactor;
                this.ship.vy += (dy / dist) * this.thrustForce * timeFactor;
                
                this.targetAngle = Math.atan2(dy, dx) * 180 / Math.PI + 90;
            }
            this.flameEl.style.opacity = '1';
        } else {
            this.flameEl.style.opacity = '0';
        }

        // Apply friction
        this.ship.vx *= Math.pow(this.friction, timeFactor);
        this.ship.vy *= Math.pow(this.friction, timeFactor);
        
        // NO MORE Force de Recul/Downward push as per feedback
        
        this.ship.x += this.ship.vx * timeFactor;
        this.ship.y += this.ship.vy * timeFactor;

        // Bounce/Clamp bounds
        if (this.ship.x < 5) { this.ship.x = 5; this.ship.vx *= -0.5; }
        if (this.ship.x > 95) { this.ship.x = 95; this.ship.vx *= -0.5; }
        if (this.ship.y < 5) { this.ship.y = 5; this.ship.vy *= -0.5; }
        if (this.ship.y > 90) { this.ship.y = 90; this.ship.vy *= -0.5; } // Ship stays in screen

        // Smooth rotation
        let angleDiff = this.targetAngle - this.currentAngle;
        while (angleDiff < -180) angleDiff += 360;
        while (angleDiff > 180) angleDiff -= 360;
        this.currentAngle += angleDiff * 0.15 * timeFactor;

        this.shipEl.style.left = `${this.ship.x}%`;
        this.shipEl.style.top = `${this.ship.y}%`;
        this.shipEl.style.transform = `translate(-50%, -50%) rotate(${this.currentAngle}deg)`;

        this.updateBackground(timeFactor);

        // 3. Track Scrolling Asteroids and Finish Line
        // Finish Line scrolls with the world
        this.finishLineY += this.scrollSpeed * timeFactor;
        this.finishLineEl.style.top = `${this.finishLineY}%`;

        this.spawnTimer += dt;
        // Stop spawning when the finish line is well into view (Sprint final)
        if (this.finishLineY < 20) {
            if (this.spawnTimer > this.spawnRate) {
                this.spawnAsteroid();
                this.spawnTimer = 0;
            }
        }

        // Asteroid movement & collision (Convert % to PX for collision)
        let sCX = (this.ship.x / 100) * this.containerWidth;
        let sCY = (this.ship.y / 100) * this.containerHeight;
        let sR = 12; // Ship actual hitbox radius

        this.updateAsteroids(timeFactor);
        
        // Check Collision for actual ship (Note: updateAsteroids already moved them)
        for (let a of this.asteroids) {
            let aCX = (a.x / 100) * this.containerWidth;
            let aCY = (a.y / 100) * this.containerHeight;
            let aR = a.size * 0.4;
            let distSq = (sCX - aCX)*(sCX - aCX) + (sCY - aCY)*(sCY - aCY);
            if (distSq < (sR + aR)*(sR + aR)) {
                this.crash();
                return;
            }
        }

        // 6. Update Particles (Explosion)
        for (let i = this.particles.length - 1; i >= 0; i--) {
            let p = this.particles[i];
            p.x += p.vx * timeFactor;
            p.y += p.vy * timeFactor;
            p.rot += p.vRot * timeFactor;
            p.life -= 0.02 * timeFactor;
            
            p.el.style.left = `${p.x}%`;
            p.el.style.top = `${p.y}%`;
            p.el.style.transform = `translate(-50%, -50%) rotate(${p.rot}deg)`;
            p.el.style.opacity = p.life;
            
            if (p.life <= 0) {
                p.el.remove();
                this.particles.splice(i, 1);
            }
        }

        // 5. Win condition: ship reaches finish line
        if (this.ship.y <= this.finishLineY + 2 && this.active && !this._isAnimating) {
            this.win();
        }
    }

    updateParticles(timeFactor) {
        // Update Particles (Explosion)
        for (let i = this.particles.length - 1; i >= 0; i--) {
            let p = this.particles[i];
            p.x += p.vx * timeFactor;
            p.y += p.vy * timeFactor;
            p.rot += p.vRot * timeFactor;
            p.life -= 0.02 * timeFactor;
            
            p.el.style.left = `${p.x}%`;
            p.el.style.top = `${p.y}%`;
            p.el.style.transform = `translate(-50%, -50%) rotate(${p.rot}deg)`;
            p.el.style.opacity = p.life;
            
            if (p.life <= 0) {
                p.el.remove();
                this.particles.splice(i, 1);
            }
        }
    }

    updateBackground(timeFactor) {
        // 2. Background Stars
        for (let star of this.stars) {
            star.y += this.scrollSpeed * star.speedMult * timeFactor;
            if (star.y > 100) star.y = -2;
            star.el.style.top = `${star.y}%`;
        }
    }

    updateAsteroids(timeFactor) {
        for (let i = this.asteroids.length - 1; i >= 0; i--) {
            let a = this.asteroids[i];
            a.x += a.dx * timeFactor;
            a.y += (this.scrollSpeed + a.dy) * timeFactor;
            a.rot += a.rotSpeed * timeFactor;
            a.el.style.left = `${a.x}%`;
            a.el.style.top = `${a.y}%`;
            a.el.style.transform = `translate(-50%, -50%) rotate(${a.rot}deg)`;

            if (a.y > 115 || a.x < -30 || a.x > 130) {
                a.el.remove();
                this.asteroids.splice(i, 1);
            }
        }
    }

    crash() {
        this._isAnimating = true;
        
        // IMPORTANT: Give the engine timer some safety padding so it doesn't call lose() before we are ready
        if (this.engine.timer !== Infinity) {
            this.engine.timer = Math.max(this.engine.timer, 2000);
        }
        
        // Shake effect
        this.container.style.transition = 'none';
        this.container.style.transform = 'translate(5px, 5px)';
        setTimeout(() => this.container.style.transform = 'translate(-5px, -5px)', 50);
        setTimeout(() => this.container.style.transform = 'translate(0, 0)', 100);

        // Hide actual ship
        this.shipVis.style.background = '#ff0000';
        this.shipVis.style.boxShadow = '0 0 40px #ff0000';
        this.shipEl.style.transition = 'opacity 0.2s, transform 0.2s';
        this.shipEl.style.transform = 'translate(-50%, -50%) scale(1.5)';
        this.shipEl.style.opacity = '0';
        this.flameEl.style.display = 'none';
        
        // Spawn more Particles
        for (let i = 0; i < 25; i++) {
            const pEl = document.createElement('div');
            pEl.style.position = 'absolute';
            const size = 5 + Math.random() * 10;
            pEl.style.width = `${size}px`;
            pEl.style.height = `${size}px`;
            pEl.style.background = (i % 3 === 0) ? '#ffffff' : (i % 2 === 0 ? this.primaryColor : this.secondaryColor);
            pEl.style.clipPath = 'polygon(50% 0%, 0% 100%, 100% 100%)';
            pEl.style.zIndex = '20';
            pEl.style.boxShadow = `0 0 10px ${this.primaryColor}`;
            
            const angle = Math.random() * Math.PI * 2;
            const speed = 0.4 + Math.random() * 2.0;
            
            this.container.appendChild(pEl);
            this.particles.push({
                el: pEl,
                x: this.ship.x,
                y: this.ship.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                rot: Math.random() * 360,
                vRot: (Math.random() - 0.5) * 40,
                life: 1.2
            });
        }
        
        setTimeout(() => {
            if(this.active) this.lose();
        }, 1800);
    }
    
    win() {
        if (!this.active) return;
        this._isAnimating = true; // freeze
        super.win();
    }
    
    lose() {
        if (!this.active) return;
        this._isAnimating = true; // freeze
        super.lose();
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        window.removeEventListener('pointerup', this.onUp);
        this.asteroids.forEach(a => a.el.remove());
        this.stars.forEach(s => s.el.remove());
        this.particles.forEach(p => p.el.remove());
        if (this.finishLineEl) this.finishLineEl.remove();
    }
}
